/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { useCallback, useEffect, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { useThemeContext } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { useAssistant } from "./apps/drive/assistant";
import { getAuthBaseUrl } from "../utils/authEnvironment";
import * as notificationsApi from "../api/notifications";
import NavSearch from "./NavSearch";
import OrgSwitcher from "./OrgSwitcher";

/**
 * Mirrors the accounts navbar: 44px bar spanning the content column only (the
 * logo lives in the sidebar), search on the left, an icon cluster and the
 * account menu on the right.
 */
const Navbar = () => {
  const theme = useTheme();
  const { themeMode, toggleTheme } = useThemeContext();
  const { user, logout } = useAuth();
  const { openPanel } = useAssistant();

  const [accountAnchor, setAccountAnchor] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);

  const refreshUnread = useCallback(async () => {
    try {
      setUnreadCount(await notificationsApi.getUnreadCount());
    } catch {
      // A notifications outage must not break the shell — leave the badge at 0.
      setUnreadCount(0);
    }
  }, []);

  useEffect(() => {
    refreshUnread();
  }, [refreshUnread]);

  const openNotifications = async (event) => {
    setNotificationAnchor(event.currentTarget);
    try {
      const { notifications: rows } = await notificationsApi.listNotifications({ pageSize: 10 });
      setNotifications(rows);
      await notificationsApi.markAllRead();
      setUnreadCount(0);
    } catch {
      setNotifications([]);
    }
  };

  const displayName =
    user?.name ||
    [user?.given_name, user?.family_name].filter(Boolean).join(" ") ||
    user?.email ||
    "Account";

  const iconButtonSx = {
    borderRadius: "12px",
    padding: "8px",
    color: theme.palette.text.color,
  };
  const iconSx = { fontSize: 20, color: theme.palette.text.color };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        height: 44,
        px: "10px",
        boxSizing: "border-box",
        backgroundColor: theme.palette.secondary.main,
        borderBottom: `1px solid ${theme.palette.border.color}`,
        zIndex: 20,
      }}
    >
      <NavSearch />

      <Tooltip title="Marketplace">
        <IconButton sx={iconButtonSx} onClick={() => window.open("https://store.vecros.com/", "_blank", "noopener")}>
          <StorefrontOutlinedIcon sx={iconSx} />
        </IconButton>
      </Tooltip>

      <Tooltip title="Help">
        <IconButton sx={iconButtonSx} onClick={() => window.open("https://discuss.vecros.com/", "_blank", "noopener")}>
          <HelpOutlineOutlinedIcon sx={iconSx} />
        </IconButton>
      </Tooltip>

      <Tooltip title={themeMode ? "Light mode" : "Dark mode"}>
        <IconButton sx={iconButtonSx} onClick={toggleTheme}>
          {themeMode ? <LightModeOutlinedIcon sx={iconSx} /> : <DarkModeOutlinedIcon sx={iconSx} />}
        </IconButton>
      </Tooltip>

      <Tooltip title="Notifications">
        <IconButton sx={iconButtonSx} onClick={openNotifications}>
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsOutlinedIcon sx={iconSx} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ my: 1, borderColor: theme.palette.border.color }} />

      <Tooltip title="Ask the Vecros assistant">
        <IconButton sx={{ ...iconButtonSx, gap: 0.75 }} onClick={() => openPanel()}>
          <AutoAwesomeIcon sx={{ fontSize: 18, color: "accent.main" }} />
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: theme.palette.text.color }}>
            Assistant
          </Typography>
        </IconButton>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ my: 1, borderColor: theme.palette.border.color }} />

      <OrgSwitcher />

      <Divider orientation="vertical" flexItem sx={{ my: 1, borderColor: theme.palette.border.color }} />

      <Box
        sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer", pl: 0.5 }}
        onClick={(e) => setAccountAnchor(e.currentTarget)}
      >
        {/* 32px matches the accounts navbar. The fill is a deliberate deviation:
            accounts uses palette.primary.main, which is the page background, so
            its avatar circle is invisible in light mode. */}
        <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: "accent.solid", color: "#fff" }}>
          {displayName.charAt(0).toUpperCase()}
        </Avatar>
        <Typography sx={{ fontSize: 13, fontWeight: 500, color: theme.palette.text.color }}>
          {displayName}
        </Typography>
        <ArrowDropDownIcon
          sx={{
            fontSize: 20,
            color: theme.palette.text.color,
            transform: accountAnchor ? "rotate(180deg)" : "none",
            transition: "0.2s",
          }}
        />
      </Box>

      <Menu
        anchorEl={notificationAnchor}
        open={Boolean(notificationAnchor)}
        onClose={() => setNotificationAnchor(null)}
        slotProps={{ paper: { sx: { width: 340, maxHeight: 400 } } }}
      >
        {notifications.length === 0 ? (
          <MenuItem disabled>
            <Typography sx={{ fontSize: 13 }}>You're all caught up.</Typography>
          </MenuItem>
        ) : (
          notifications.map((n) => (
            <MenuItem key={n.id} sx={{ whiteSpace: "normal", alignItems: "flex-start" }}>
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{n.title}</Typography>
                <Typography sx={{ fontSize: 12, color: theme.palette.secondaryText.color }}>
                  {n.message || n.body}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
      </Menu>

      <Menu
        anchorEl={accountAnchor}
        open={Boolean(accountAnchor)}
        onClose={() => setAccountAnchor(null)}
        slotProps={{ paper: { sx: { width: 260 } } }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{displayName}</Typography>
          {user?.email && (
            <Typography sx={{ fontSize: 12, color: theme.palette.secondaryText.color }}>
              {user.email}
            </Typography>
          )}
        </Box>
        <Divider />
        {/* Profile, billing and security live in accounts — link out rather than
            reimplement them here. */}
        <MenuItem component="a" href={`${getAuthBaseUrl()}/profile`} sx={{ fontSize: 13 }}>
          Profile & settings
        </MenuItem>
        <MenuItem component="a" href={`${getAuthBaseUrl()}/billing`} sx={{ fontSize: 13 }}>
          Billing
        </MenuItem>
        <Divider />
        <MenuItem
          sx={{ fontSize: 13 }}
          onClick={() => {
            setAccountAnchor(null);
            logout();
          }}
        >
          Sign out
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Navbar;
